import { motion, AnimatePresence } from 'framer-motion'
import 'primeicons/primeicons.css'

interface RulesModalProps {
  isOpen: boolean
  onClose: () => void
}

export default function RulesModal({ isOpen, onClose }: RulesModalProps) {
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-br from-gold-900/30 to-black/90 border-2 border-gold-500 rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto custom-scrollbar">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b-2 border-gold-500/50">
                <div>
                  <h3 className="text-2xl md:text-3xl font-bold text-gold-400 mb-1">
                    LUẬT CHƠI
                  </h3>
                  <p className="text-sm text-gray-400">
                    THỬ THÁCH KỶ LUẬT TẬP LUYỆN – SAIGON FC
                  </p>
                </div>
                <button
                  onClick={onClose}
                  className="w-10 h-10 flex items-center justify-center text-gold-400 hover:text-gold-300 hover:bg-gold-500/20 rounded-lg transition"
                >
                  <i className="pi pi-times text-xl"></i>
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-6 space-y-6 text-gray-300">
                {/* 1️⃣ MỤC TIÊU */}
                <div>
                  <h4 className="text-xl font-bold text-gold-400 mb-3">
                    1️⃣ MỤC TIÊU
                  </h4>
                  <p className="mb-2">Thử thách được tạo ra để giúp học viên:</p>
                  <ul className="space-y-2 ml-4">
                    <li className="flex items-start gap-2">
                      <span className="text-gold-400">•</span>
                      <span>Duy trì thói quen tập luyện mỗi ngày</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-gold-400">•</span>
                      <span>Cải thiện cảm giác bóng và thể lực</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-gold-400">•</span>
                      <span>Tạo môi trường thi đua lành mạnh, công bằng và minh bạch</span>
                    </li>
                  </ul>
                  <p className="mt-2 text-gold-400 font-semibold">
                    Mỗi tháng là một cuộc đua mới, tất cả cùng bắt đầu lại từ 0.
                  </p>
                </div>

                {/* 2️⃣ ĐỐI TƯỢNG THAM GIA */}
                <div>
                  <h4 className="text-xl font-bold text-gold-400 mb-3">
                    2️⃣ ĐỐI TƯỢNG THAM GIA
                  </h4>
                  <ul className="space-y-2 ml-4">
                    <li className="flex items-start gap-2">
                      <span className="text-gold-400">•</span>
                      <span>Dành cho học viên của Saigon FC</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-gold-400">•</span>
                      <span>Mỗi người tham gia bằng 01 tài khoản duy nhất</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-gold-400">•</span>
                      <span>Người chơi được tự đặt tên hiển thị và upload ảnh đại diện</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-400">❌</span>
                      <span>Coach / Admin không tham gia thi đua, chỉ quản trị hệ thống.</span>
                    </li>
                  </ul>
                </div>

                {/* 3️⃣ CÁCH ĐIỂM DANH & CỘNG ĐIỂM */}
                <div>
                  <h4 className="text-xl font-bold text-gold-400 mb-3">
                    3️⃣ CÁCH ĐIỂM DANH & CỘNG ĐIỂM
                  </h4>
                  <div className="space-y-3">
                    <div>
                      <p className="font-semibold text-gold-400 mb-2">🔹 Điểm danh hằng ngày</p>
                      <ul className="space-y-2 ml-4">
                        <li className="flex items-start gap-2">
                          <span className="text-gold-400">•</span>
                          <span>Mỗi ngày, người chơi bấm "Đã tập hôm nay"</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-gold-400">•</span>
                          <span>Cộng +1 điểm / ngày</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-gold-400">•</span>
                          <span>Mỗi ngày chỉ được điểm danh 1 lần</span>
                        </li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-semibold text-gold-400 mb-2">🔹 Bằng chứng hợp lệ</p>
                      <p className="ml-4 mb-2">
                        Upload ảnh chụp màn hình comment dưới bài điểm danh của Coach trên nhóm chung. Trong ảnh nên thể hiện ngày tập và nội dung tập luyện (khuyến khích).
                      </p>
                      <p className="ml-4 text-amber-400">
                        ⚠️ Không có bằng chứng → hệ thống vẫn ghi nhận điểm danh, nhưng có thể bị trừ điểm nếu phát hiện gian lận.
                      </p>
                    </div>
                  </div>
                </div>

                {/* 4️⃣ QUY ĐỊNH VỀ THỜI GIAN */}
                <div>
                  <h4 className="text-xl font-bold text-gold-400 mb-3">
                    4️⃣ QUY ĐỊNH VỀ THỜI GIAN
                  </h4>
                  <ul className="space-y-2 ml-4">
                    <li className="flex items-start gap-2">
                      <span className="text-gold-400">•</span>
                      <span>Không bù điểm cho ngày đã qua</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-gold-400">•</span>
                      <span>Quên điểm danh → không được cộng điểm</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-gold-400">•</span>
                      <span>Vị trí trên bảng xếp hạng có thể bị tụt nếu người khác tập đều hơn</span>
                    </li>
                  </ul>
                </div>

                {/* 5️⃣ ĐIỂM THƯỞNG THEO SỐ NGÀY TẬP */}
                <div>
                  <h4 className="text-xl font-bold text-gold-400 mb-3">
                    5️⃣ ĐIỂM THƯỞNG THEO SỐ NGÀY TẬP
                  </h4>
                  <div className="bg-black/40 rounded-lg p-4 space-y-2">
                    <div className="flex justify-between items-center">
                      <span>3 ngày:</span>
                      <span className="text-gold-400 font-bold">+1 điểm</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span>7 ngày:</span>
                      <span className="text-gold-400 font-bold">+2 điểm</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span>14 ngày:</span>
                      <span className="text-gold-400 font-bold">+3 điểm</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span>21 ngày:</span>
                      <span className="text-gold-400 font-bold">+4 điểm</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span>30 ngày:</span>
                      <span className="text-gold-400 font-bold">+5 điểm</span>
                    </div>
                  </div>
                  <p className="mt-2 text-sm text-gray-400">
                    📌 Chỉ cần đạt đủ số ngày trong tháng, không yêu cầu tập liên tục.
                  </p>
                </div>

                {/* 6️⃣ CHUỖI & HUY HIỆU */}
                <div>
                  <h4 className="text-xl font-bold text-gold-400 mb-3">
                    6️⃣ CHUỖI & HUY HIỆU
                  </h4>
                  <p>
                    Người chơi sẽ nhận huy hiệu khi đạt các mốc: <strong className="text-gold-400">3 – 7 – 14 – 21 – 30 ngày tập</strong>. Huy hiệu mang tính khích lệ tinh thần.
                  </p>
                </div>

                {/* 7️⃣ BẢNG XẾP HẠNG */}
                <div>
                  <h4 className="text-xl font-bold text-gold-400 mb-3">
                    7️⃣ BẢNG XẾP HẠNG
                  </h4>
                  <p>
                    Bảng xếp hạng được cập nhật liên tục với: <strong className="text-gold-400">Thứ hạng, Tên & ảnh đại diện, Tổng điểm, hành trình tập luyện (X/30), Chuỗi cao nhất, Mũi tên ↑ ↓</strong> thể hiện thay đổi thứ hạng.
                  </p>
                </div>

                {/* 8️⃣ PHÂN ĐỊNH KHI BẰNG ĐIỂM */}
                <div>
                  <h4 className="text-xl font-bold text-gold-400 mb-3">
                    8️⃣ PHÂN ĐỊNH KHI BẰNG ĐIỂM
                  </h4>
                  <ul className="space-y-2 ml-4">
                    <li className="flex items-start gap-2">
                      <span className="text-gold-400">•</span>
                      <span>Tổng điểm cao hơn</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-gold-400">•</span>
                      <span>Số buổi tập nhiều hơn</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-gold-400">•</span>
                      <span>Chuỗi max cao hơn</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-gold-400">•</span>
                      <span>Điểm danh gần nhất</span>
                    </li>
                  </ul>
                </div>

                {/* 9️⃣ KẾT THÚC THÁNG */}
                <div>
                  <h4 className="text-xl font-bold text-gold-400 mb-3">
                    🔟 KẾT THÚC THÁNG
                  </h4>
                  <p>
                    Cuối mỗi tháng: Hệ thống tổng kết Top 10, vinh danh Top 1 – 2 – 3. Điểm và dữ liệu tháng hiện tại sẽ được reset. <strong className="text-gold-400">Tháng mới bắt đầu = cuộc đua mới!</strong>
                  </p>
                </div>

                {/* 1️⃣1️⃣ QUY ĐỊNH CHUNG */}
                <div>
                  <h4 className="text-xl font-bold text-gold-400 mb-3">
                    1️⃣1️⃣ QUY ĐỊNH CHUNG
                  </h4>
                  <ul className="space-y-2 ml-4">
                    <li className="flex items-start gap-2">
                      <span className="text-gold-400">•</span>
                      <span>Gian lận, spam hoặc sử dụng nhiều tài khoản có thể bị trừ điểm hoặc loại</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-gold-400">•</span>
                      <span>Quyết định cuối cùng thuộc về Ban quản trị Saigon FC</span>
                    </li>
                  </ul>
                </div>

                {/* Quote */}
                <div className="text-center pt-4 border-t border-gold-500/30">
                  <p className="text-xl font-bold text-gold-400 italic">
                    "Kỷ luật mỗi ngày – Tiến bộ mỗi tháng"
                  </p>
                  <p className="text-sm text-gray-400 mt-2">Saigon FC</p>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="p-6 border-t-2 border-gold-500/50 flex justify-end">
                <button
                  onClick={onClose}
                  className="px-6 py-2 bg-gold-gradient text-white font-bold rounded-lg hover:scale-105 transition"
                >
                  Đã hiểu
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}